import React, { useId, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useManifestExport from '../utils/useManifestExport';
import './styles/manifestPopupCard.css';

function localDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
}

function ExportManifestPopup({ onCancel }) {
  const exp = useManifestExport();
  const [startDate, setStartDate] = useState(() => localDate(exp.job?.start_date));
  const [endDate, setEndDate] = useState(() => localDate(exp.job?.end_date));
  const [validation, setValidation] = useState('');
  const dateId = useId();
  const active = exp.job && ['pending', 'processing'].includes(exp.job.status);
  const busy = exp.creating || active;
  const progress = typeof exp.job?.progress === 'number' ? Math.min(100, Math.max(0, exp.job.progress)) : null;

  const submit = event => {
    event.preventDefault();
    if (!startDate || !endDate) { setValidation('Choose both a start date and an end date.'); return; }
    if (startDate > endDate) { setValidation('Start date must be on or before end date.'); return; }
    setValidation('');
    exp.start(formatDate(startDate), formatDate(endDate));
  };

  return (
    <form className="manifest-export-form" onSubmit={submit}>
      <div className="date-picker manifest-pop-date">
        <div className="manifest-date-field">
          <label htmlFor={`${dateId}-start`}>Start date</label>
          <DatePicker id={`${dateId}-start`} selected={startDate}
            onChange={date => { setStartDate(date); setValidation(''); }}
            shouldCloseOnSelect
            selectsStart startDate={startDate} endDate={endDate} maxDate={endDate}
            placeholderText="Choose date" dateFormat="dd MMM yyyy" disabled={busy} />
        </div>
        <div className="manifest-date-field">
          <label htmlFor={`${dateId}-end`}>End date</label>
          <DatePicker id={`${dateId}-end`} selected={endDate}
            onChange={date => { setEndDate(date); setValidation(''); }}
            shouldCloseOnSelect
            selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
            placeholderText="Choose date" dateFormat="dd MMM yyyy" disabled={busy} />
        </div>
      </div>
      {(validation || exp.error) && <p className="manifest-export-error" role="alert">{validation || exp.error}</p>}
      {(exp.creating || exp.job) && (
        <section className={`manifest-export-status${exp.job?.status === 'failed' ? ' is-failed' : ''}`} aria-live="polite">
          <h3>{exp.creating ? 'Preparing export…' : {
            pending: 'Preparing export…', processing: 'Exporting data…', completed: 'Export ready', failed: 'Export failed',
          }[exp.job.status]}</h3>
          {exp.job && <p className="manifest-export-range">{exp.job.start_date} → {exp.job.end_date}</p>}
          {active && (
            <div className="manifest-export-progress">
              <progress aria-label="Manifest export progress" max="100" {...(progress === null ? {} : { value: progress })} />
              {progress !== null && <span>{progress}%</span>}
            </div>
          )}
          {exp.job?.status === 'failed'
            ? <p className="manifest-export-error">{exp.job.error || 'Could not generate the export. Please try again.'}</p>
            : exp.job?.status === 'completed'
              ? <p>Your CSV is ready. Download it when convenient.</p>
              : <p>You can continue using the app while we prepare your CSV. Close this dialog and reopen it to check your export.</p>}
        </section>
      )}
      {exp.pollError && <p className="manifest-export-connection" role="status">{exp.pollError}</p>}
      <div className="manifest-export-actions">
        {onCancel && <button type="button" className="manifest-export-secondary" onClick={onCancel}>Continue using app</button>}
        {exp.pollPaused && <button type="button" className="manifest-export-secondary" onClick={exp.checkAgain}>Check again</button>}
        {exp.pollPaused && active && <button type="button" className="manifest-export-secondary" onClick={exp.reset}>Choose another export</button>}
        {exp.job?.status === 'completed' ? (
          <>
            <button type="button" className="manifest-export-secondary" onClick={exp.reset} disabled={exp.downloading}>New export</button>
            <button type="button" className="export-button" onClick={exp.download} disabled={exp.downloading} aria-busy={exp.downloading}>
              {exp.downloading ? 'Downloading…' : 'Download CSV'}
            </button>
          </>
        ) : (
          <button type="submit" className={`export-button${exp.creating ? ' is-loading' : ''}`} disabled={busy} aria-busy={exp.creating}>
            {exp.creating && <span className="export-button-spinner" aria-hidden="true" />}
            {exp.creating ? 'Preparing…' : active ? 'Export in progress' : exp.job?.status === 'failed' ? 'Try Again' : 'Export CSV'}
          </button>
        )}
      </div>
    </form>
  );
}

export default ExportManifestPopup;
